import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  Circle,
  FileText,
  FolderOpen,
  History,
  MinusCircle,
  Rocket,
  XCircle,
} from 'lucide-react';
import { useCompanies } from '../../hooks/useCompanies';
import { useActiveCompanyId } from '../../store/useCompanyStore';
import { Select } from '../ui/Select';
import { ApiError } from '../../lib/api';
import {
  getOnboardingSteps,
  listImportBatches,
  setOnboardingStep,
  type ImportBatchRow,
  type OnboardingStepKey,
  type OnboardingStepState,
  type OnboardingStepStatus,
} from '../../lib/onboardingImportApi';
import {
  getStatement,
  listStatements,
  reconcileStatement,
  toReconcileLine,
  type StatementSummary,
} from '../../lib/bankStatementsApi';
import { getFinancialReport } from '../../lib/monthlyReportApi';
import { defaultReportPeriod } from '../../lib/reportPeriod';
import { ExcelImportDialog } from '../directory/ExcelImportDialog';
import { InvoiceImportDialog } from '../domain/invoices/InvoiceImportDialog';
import { ReconciliationImportDialog } from '../bankStatements/ReconciliationImportDialog';
import { BankAccountsSettings } from '../settings/BankAccountsSettings';
import { StartWizard } from './StartWizard';
import { TeamImportDialog } from './TeamImportDialog';
import { StatementsBatchUpload } from './StatementsBatchUpload';

// Setări → Onboarding (spec import istoric financiar, pasul 7). One firm at a
// time (picker on top). Two ways in:
//   - „Pornesc de la zero”: the setup wizard that used to live at /onboarding;
//   - „Am fișierele de onboarding 00–08”: the financial history from the
//     accounting folder, in nine steps that reuse the imports of each page.
// Every step's state is stored per firm (done / skipped / error), with the
// import batch of its last run, so the page can be resumed. The `mode` row
// keeps the choice: done = files, skipped = from zero.

interface HistoryStep {
  key: Exclude<OnboardingStepKey, 'mode'>;
  title: string;
  file: string | null;
  doc: string | null;
  what: string;
}

const HISTORY_STEPS: HistoryStep[] = [
  {
    key: 'categorii',
    title: 'Categorii',
    file: '00_VERIFICARE.xlsx (foaia CATEGORII)',
    doc: 'onboarding-categorii-v1.md',
    what: 'Categoriile de venituri și cheltuieli ale firmei (Cod + Denumire).',
  },
  {
    key: 'conturi',
    title: 'Conturi bancare',
    file: null,
    doc: null,
    what: 'Conturile firmei (IBAN, bancă, valută), de mână. Extrasele se leagă de ele după IBAN.',
  },
  {
    key: 'echipa',
    title: 'Echipă',
    file: '06_echipa_subcontractori.xlsx',
    doc: 'onboarding-echipa-v1.md',
    what: 'Oamenii și colaboratorii B2B, cu firma, CUI-ul și IBAN-ul lor, și proiectele pe perioade.',
  },
  {
    key: 'clienti',
    title: 'Clienți',
    file: '01_clienti.xlsx',
    doc: 'onboarding-clienti-v1.md',
    what: 'Clienții firmei, cu CIF (categoria de venit vine din reconciliere, pasul 9).',
  },
  {
    key: 'furnizori',
    title: 'Furnizori',
    file: '02_furnizori.xlsx',
    doc: 'onboarding-furnizori-v1.md',
    what: 'Furnizorii, cu CUI, IBAN și categoria de cheltuială.',
  },
  {
    key: 'abonamente',
    title: 'Abonamente',
    file: '03_abonamente.xlsx',
    doc: 'onboarding-abonamente-v1.md',
    what: 'Abonamentele plătite cu cardul, cu tiparul comerciantului de pe extras.',
  },
  {
    key: 'facturi',
    title: 'Facturi',
    file: '04_facturi.xlsx',
    doc: 'onboarding-facturi-v1.md',
    what: 'Istoricul facturilor emise și primite; cele din SPV nu se dublează.',
  },
  {
    key: 'extrase',
    title: 'Extrase',
    file: 'PDF-urile extraselor',
    doc: null,
    what: 'Toate extrasele, deodată; se încarcă pe rând, în ordine cronologică.',
  },
  {
    key: 'reconciliere',
    title: 'Reconciliere',
    file: '07_reconciliere_import.xlsx',
    doc: 'onboarding-reconciliere-v1.md',
    what: 'Deciziile pe fiecare linie de extras; apoi finalizezi extrasele, în ordine.',
  },
];

const STATUS_LABEL: Record<OnboardingStepStatus, string> = {
  pending: 'De făcut',
  done: 'Gata',
  skipped: 'Sărit',
  error: 'Cu erori',
};

const KIND_LABEL: Record<string, string> = {
  categorii: 'Categorii',
  echipa: 'Echipă',
  clienti: 'Clienți',
  furnizori: 'Furnizori',
  abonamente: 'Abonamente',
  facturi: 'Facturi',
  reconciliere: 'Reconciliere',
};

const stepsKey = (companyId: string) => ['onboarding', 'steps', companyId] as const;
const batchesKey = (companyId: string) => ['onboarding', 'batches', companyId] as const;

export function OnboardingSettings() {
  const { data: companies } = useCompanies();
  const activeCompanyId = useActiveCompanyId();
  const [picked, setPicked] = useState<string | null>(null);
  const companyId =
    picked ??
    activeCompanyId ??
    companies?.find((c) => c.isDefault)?.id ??
    companies?.[0]?.id ??
    null;

  const stepsQuery = useQuery({
    queryKey: stepsKey(companyId ?? ''),
    queryFn: () => getOnboardingSteps(companyId as string),
    enabled: Boolean(companyId),
  });
  const qc = useQueryClient();
  const steps = useMemo(() => {
    const map = new Map<OnboardingStepKey, OnboardingStepState>();
    for (const s of stepsQuery.data?.steps ?? []) map.set(s.step, s);
    return map;
  }, [stepsQuery.data]);
  const mode = steps.get('mode')?.status;
  const [error, setError] = useState<string | null>(null);

  async function record(
    step: OnboardingStepKey,
    status: OnboardingStepStatus,
    importBatchId?: string | null,
  ) {
    if (!companyId) return;
    setError(null);
    try {
      await setOnboardingStep(step, { companyId, status, importBatchId });
      await qc.invalidateQueries({ queryKey: stepsKey(companyId) });
      await qc.invalidateQueries({ queryKey: batchesKey(companyId) });
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : 'Nu s-a salvat.');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-[260px] max-w-sm flex-1">
          {companies && companies.length > 0 && (
            <Select
              label="Firma"
              value={companyId ?? ''}
              onChange={(v) => setPicked(v)}
              options={companies.map((c) => ({ value: c.id, label: c.displayName || c.legalName }))}
            />
          )}
        </div>
        {mode && (
          <button
            type="button"
            onClick={() => void record('mode', 'pending')}
            className="text-sm text-brand-700 hover:underline"
          >
            Schimbă alegerea
          </button>
        )}
      </div>

      {error && (
        <p className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <XCircle className="h-4 w-4" /> {error}
        </p>
      )}

      {stepsQuery.isLoading && <p className="text-sm text-gray-500">Se încarcă progresul…</p>}

      {companyId && stepsQuery.data && (!mode || mode === 'pending') && (
        <div className="grid gap-4 sm:grid-cols-2">
          <ChoiceCard
            icon={<Rocket className="h-6 w-6 text-brand-600" />}
            title="Pornesc de la zero"
            text="Profilul firmei, integrările, primii clienți, un Excel cu echipa și proiectele, furnizori, task-uri."
            onClick={() => void record('mode', 'skipped')}
          />
          <ChoiceCard
            icon={<FolderOpen className="h-6 w-6 text-brand-600" />}
            title="Am fișierele de onboarding 00–08"
            text="Istoricul financiar din folderul de contabilitate: categorii, echipă, clienți, furnizori, abonamente, facturi, extrase și reconcilierea lor."
            onClick={() => void record('mode', 'done')}
          />
        </div>
      )}

      {companyId && mode === 'skipped' && <StartWizard />}

      {companyId && mode === 'done' && (
        <HistoryWizard companyId={companyId} steps={steps} record={record} />
      )}

      {companyId && <ImportHistory companyId={companyId} />}
    </div>
  );
}

function ChoiceCard({
  icon,
  title,
  text,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-lg border border-gray-200 bg-white p-5 text-left shadow-sm hover:border-brand-400 hover:shadow"
    >
      {icon}
      <p className="mt-3 text-base font-semibold text-gray-900">{title}</p>
      <p className="mt-1 text-sm text-gray-600">{text}</p>
    </button>
  );
}

// --- Istoric financiar ---------------------------------------------------------

type Dialog = Exclude<OnboardingStepKey, 'mode' | 'conturi' | 'extrase'> | null;

function HistoryWizard({
  companyId,
  steps,
  record,
}: {
  companyId: string;
  steps: Map<OnboardingStepKey, OnboardingStepState>;
  record: (
    step: OnboardingStepKey,
    status: OnboardingStepStatus,
    batch?: string | null,
  ) => Promise<void>;
}) {
  const [open, setOpen] = useState<Dialog>(null);
  const [doc, setDoc] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<OnboardingStepKey | null>(
    () =>
      HISTORY_STEPS.find((s) => !['done', 'skipped'].includes(steps.get(s.key)?.status ?? ''))
        ?.key ?? null,
  );
  const statements = useQuery({
    queryKey: ['onboarding', 'statements', companyId],
    queryFn: () => allStatements(companyId),
  });
  // A run with failed rows leaves the step „Cu erori”, not „Gata”.
  const finished =
    (key: OnboardingStepKey) =>
    (batchId?: string | null, failed = 0) =>
      void record(key, failed > 0 ? 'error' : 'done', batchId ?? null);

  return (
    <div className="space-y-6">
      <ControlFigures steps={steps} />

      <ol className="space-y-2">
        {HISTORY_STEPS.map((step, i) => {
          const state = steps.get(step.key);
          const status = state?.status ?? 'pending';
          const isOpen = expanded === step.key;
          return (
            <li key={step.key} className="rounded-lg border border-gray-200 bg-white">
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : step.key)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left"
              >
                <StepIcon status={status} />
                <span className="text-sm font-semibold text-gray-900">
                  {i + 1}. {step.title}
                </span>
                {step.file && (
                  <span className="hidden truncate font-mono text-xs text-gray-500 sm:inline">
                    {step.file}
                  </span>
                )}
                <span className="ml-auto text-xs text-gray-500">{STATUS_LABEL[status]}</span>
              </button>
              {isOpen && (
                <div className="space-y-3 border-t border-gray-100 px-4 py-3">
                  <p className="text-sm text-gray-600">{step.what}</p>
                  {step.doc && (
                    <button
                      type="button"
                      onClick={() => setDoc(step.doc)}
                      className="inline-flex items-center gap-1 text-xs text-brand-700 hover:underline"
                    >
                      <FileText className="h-3.5 w-3.5" /> Formatul fișierului ({step.doc})
                    </button>
                  )}

                  {step.key === 'conturi' && <BankAccountsSettings />}
                  {step.key === 'extrase' && (
                    <StatementsBatchUpload
                      companyId={companyId}
                      onUploaded={() => void statements.refetch()}
                    />
                  )}
                  {step.key === 'reconciliere' && (
                    <FinalizeList
                      statements={statements.data?.rows ?? []}
                      onChanged={() => void statements.refetch()}
                    />
                  )}

                  <div className="flex flex-wrap items-center gap-2">
                    {step.key !== 'conturi' && step.key !== 'extrase' && (
                      <button
                        type="button"
                        onClick={() => setOpen(step.key as Dialog)}
                        className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
                      >
                        Încarcă fișierul
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => void record(step.key, 'done')}
                      className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Marchează gata
                    </button>
                    <button
                      type="button"
                      onClick={() => void record(step.key, 'skipped')}
                      className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
                    >
                      Sari peste
                    </button>
                    {status !== 'pending' && (
                      <button
                        type="button"
                        onClick={() => void record(step.key, 'pending')}
                        className="text-xs text-gray-500 hover:underline"
                      >
                        Redeschide
                      </button>
                    )}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ol>

      <YearTotals companyId={companyId} />

      {doc && <FormatDoc name={doc} onClose={() => setDoc(null)} />}

      {open === 'categorii' && (
        <ExcelImportDialog
          entityType="expense-categories"
          companyId={companyId}
          onClose={() => setOpen(null)}
          onImportComplete={(r) => finished('categorii')(r?.batchId, r?.failed.length)}
        />
      )}
      {(open === 'clienti' || open === 'furnizori' || open === 'abonamente') && (
        <ExcelImportDialog
          entityType={
            open === 'clienti' ? 'clients' : open === 'furnizori' ? 'suppliers' : 'subscriptions'
          }
          companyId={companyId}
          onClose={() => setOpen(null)}
          onImportComplete={(r) => finished(open)(r?.batchId, r?.failed.length)}
        />
      )}
      {open === 'echipa' && (
        <TeamImportDialog
          companyId={companyId}
          onClose={() => setOpen(null)}
          onImportComplete={(r) => finished('echipa')(r.batchId, r.failed.length)}
        />
      )}
      {open === 'facturi' && (
        <InvoiceImportDialog
          companyId={companyId}
          onClose={() => setOpen(null)}
          onImportComplete={(r) => finished('facturi')(r?.batchId, r?.failed.length)}
        />
      )}
      {open === 'reconciliere' && (
        <ReconciliationImportDialog
          companyId={companyId}
          statements={(statements.data?.rows ?? []).map((s) => ({
            id: s.id,
            label: statementLabel(s),
          }))}
          onClose={() => {
            setOpen(null);
            void statements.refetch();
          }}
          onImported={(r) => finished('reconciliere')(r.batchId, r.errors)}
        />
      )}
    </div>
  );
}

/**
 * A file format, as documented in docs/formate (copied into the frontend image
 * at /formate/ — see frontend/Dockerfile), so a client can produce the files
 * with their own accountant.
 */
function FormatDoc({ name, onClose }: { name: string; onClose: () => void }) {
  const query = useQuery({
    queryKey: ['onboarding', 'format', name],
    queryFn: async () => {
      const res = await fetch(`/formate/${name}`);
      const text = res.ok ? await res.text() : '';
      // The SPA fallback answers unknown paths with index.html.
      if (!text || text.trimStart().startsWith('<')) throw new Error('missing');
      return text;
    },
    retry: false,
  });
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <h2 className="font-mono text-sm font-semibold text-gray-900">{name}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Închide"
          >
            ✕
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4">
          {query.isLoading && <p className="text-sm text-gray-500">Se încarcă…</p>}
          {query.isError && (
            <p className="text-sm text-gray-600">
              Documentul nu e disponibil aici; îl găsești în repo, la{' '}
              <span className="font-mono">docs/formate/{name}</span>.
            </p>
          )}
          {query.data && (
            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800">{query.data}</pre>
          )}
        </div>
      </div>
    </div>
  );
}

function StepIcon({ status }: { status: OnboardingStepStatus }) {
  if (status === 'done') return <CheckCircle2 className="h-5 w-5 text-green-600" />;
  if (status === 'skipped') return <MinusCircle className="h-5 w-5 text-gray-400" />;
  if (status === 'error') return <XCircle className="h-5 w-5 text-red-600" />;
  return <Circle className="h-5 w-5 text-gray-300" />;
}

/** The counts of each step's last import: imported, already there, failed. */
function ControlFigures({ steps }: { steps: Map<OnboardingStepKey, OnboardingStepState> }) {
  const rows = HISTORY_STEPS.map((s) => ({ step: s, batch: steps.get(s.key)?.importBatch })).filter(
    (r) => r.batch?.stats,
  );
  if (rows.length === 0) return null;
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
          <tr>
            <th className="px-3 py-2">Pas</th>
            <th className="px-3 py-2 text-right">Rânduri</th>
            <th className="px-3 py-2 text-right">Importate</th>
            <th className="px-3 py-2 text-right">Existau deja</th>
            <th className="px-3 py-2 text-right">Sărite</th>
            <th className="px-3 py-2 text-right">Erori</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map(({ step, batch }) => {
            const st = batch?.stats ?? {};
            const n = (k: string) => (typeof st[k] === 'number' ? st[k] : 0);
            return (
              <tr key={step.key}>
                <td className="px-3 py-2 text-gray-900">{step.title}</td>
                <td className="px-3 py-2 text-right">{n('rows')}</td>
                <td className="px-3 py-2 text-right text-green-700">
                  {n('created') + n('updated') + n('applied')}
                </td>
                <td className="px-3 py-2 text-right text-gray-600">{n('unchanged')}</td>
                <td className="px-3 py-2 text-right text-amber-700">{n('skipped')}</td>
                <td className="px-3 py-2 text-right text-red-700">{n('failed') + n('errors')}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Every statement of the firm (the list is paged by at most 100). */
async function allStatements(companyId: string): Promise<{ rows: StatementSummary[] }> {
  const rows: StatementSummary[] = [];
  for (let page = 1; ; page++) {
    const res = await listStatements(page, 100, companyId, { sort: 'periodStart' });
    rows.push(...res.rows);
    if (page >= res.meta.totalPages) break;
  }
  return { rows };
}

function statementLabel(s: StatementSummary): string {
  const account = s.bankAccount?.label || s.bankAccount?.iban || s.iban || s.bankName;
  return `${account} · ${s.periodStart ?? '?'} → ${s.periodEnd ?? '?'}`;
}

/** The firm's statements still to finalize, oldest first per account. */
function FinalizeList({
  statements,
  onChanged,
}: {
  statements: StatementSummary[];
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const open = [...statements]
    .filter((s) => s.status !== 'RECONCILED')
    .sort(
      (a, b) =>
        (a.iban ?? '').localeCompare(b.iban ?? '') ||
        (a.periodStart ?? '').localeCompare(b.periodStart ?? ''),
    );
  if (statements.length === 0) {
    return (
      <p className="text-sm text-gray-500">Niciun extras încărcat încă pentru firma aceasta.</p>
    );
  }
  if (open.length === 0) {
    return (
      <p className="flex items-center gap-2 text-sm text-green-700">
        <CheckCircle2 className="h-4 w-4" /> Toate cele {statements.length} extrase sunt finalizate.
      </p>
    );
  }

  async function finalize(id: string) {
    setBusy(id);
    setErrors((e) => ({ ...e, [id]: '' }));
    try {
      // The decisions as they are (imported or made on the reconcile page):
      // finalizing sends every line back, unchanged.
      const statement = await getStatement(id);
      await reconcileStatement(id, statement.transactions.map(toReconcileLine), true);
      onChanged();
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [id]: err instanceof Error ? err.message : 'Finalizarea a eșuat.',
      }));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-md border border-gray-200">
      <p className="border-b border-gray-100 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">
        De finalizat, în ordine ({open.length} din {statements.length})
      </p>
      <ul className="divide-y divide-gray-100">
        {open.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
            <span className="text-gray-900">{statementLabel(s)}</span>
            <span className="text-xs text-gray-500">{s.transactionCount} linii</span>
            <span className="ml-auto flex items-center gap-2">
              <Link
                to={`/financials/bank-statements/${s.id}/reconcile`}
                className="text-xs text-brand-700 hover:underline"
              >
                Deschide
              </Link>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => void finalize(s.id)}
                className="rounded-md bg-brand-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {busy === s.id ? 'Se finalizează…' : 'Finalizează'}
              </button>
            </span>
            {errors[s.id] && <span className="w-full text-xs text-red-700">{errors[s.id]}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Totals per year, to compare with the PE CATEGORII SI ANI sheet of 05. */
function YearTotals({ companyId }: { companyId: string }) {
  const now = new Date().getFullYear();
  const years = [now - 3, now - 2, now - 1, now];
  const query = useQuery({
    queryKey: ['onboarding', 'year-totals', companyId, years],
    queryFn: () =>
      Promise.all(
        years.map(async (year) => {
          const r = await getFinancialReport(
            { ...defaultReportPeriod(), kind: 'year', year },
            [companyId],
            true,
          );
          return { year, summary: r.report.summary };
        }),
      ),
  });
  const fmt = (n: number) => n.toLocaleString('ro-RO', { maximumFractionDigits: 0 });
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <p className="border-b border-gray-100 px-4 py-2 text-sm font-semibold text-gray-900">
        Totaluri pe an (RON) — de comparat cu foaia „PE CATEGORII SI ANI” din 05
      </p>
      {query.isLoading && <p className="px-4 py-3 text-sm text-gray-500">Se calculează…</p>}
      {query.isError && (
        <p className="px-4 py-3 text-sm text-red-600">Totalurile nu s-au putut calcula.</p>
      )}
      {query.data && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
              <tr>
                <th className="px-4 py-2">An</th>
                <th className="px-4 py-2 text-right">Venituri</th>
                <th className="px-4 py-2 text-right">Cheltuieli</th>
                <th className="px-4 py-2 text-right">Finanțare intrări</th>
                <th className="px-4 py-2 text-right">Finanțare ieșiri</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {query.data.map(({ year, summary }) => (
                <tr key={year}>
                  <td className="px-4 py-2 text-gray-900">{year}</td>
                  <td className="px-4 py-2 text-right text-green-700">{fmt(summary.inflows)}</td>
                  <td className="px-4 py-2 text-right text-red-700">{fmt(summary.outflows)}</td>
                  <td className="px-4 py-2 text-right">{fmt(summary.financing?.inflows ?? 0)}</td>
                  <td className="px-4 py-2 text-right">{fmt(summary.financing?.outflows ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Every import run of the firm; a reconciliation one opens its „din import” filter. */
function ImportHistory({ companyId }: { companyId: string }) {
  const query = useQuery({
    queryKey: batchesKey(companyId),
    queryFn: () => listImportBatches(companyId),
  });
  const rows: ImportBatchRow[] = query.data ?? [];
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <p className="flex items-center gap-2 border-b border-gray-100 px-4 py-2 text-sm font-semibold text-gray-900">
        <History className="h-4 w-4 text-gray-500" /> Istoric importuri
      </p>
      {query.isLoading && <p className="px-4 py-3 text-sm text-gray-500">Se încarcă…</p>}
      {query.data && rows.length === 0 && (
        <p className="px-4 py-3 text-sm text-gray-500">Niciun import pentru firma aceasta.</p>
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
              <tr>
                <th className="px-4 py-2">Data</th>
                <th className="px-4 py-2">Ce</th>
                <th className="px-4 py-2">Fișier</th>
                <th className="px-4 py-2">Cine</th>
                <th className="px-4 py-2">Rezultat</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((b) => {
                const st = b.stats ?? {};
                const n = (k: string) => (typeof st[k] === 'number' ? st[k] : 0);
                return (
                  <tr key={b.id}>
                    <td className="whitespace-nowrap px-4 py-2 text-gray-600">
                      {new Date(b.createdAt).toLocaleString('ro-RO')}
                    </td>
                    <td className="px-4 py-2 text-gray-900">{KIND_LABEL[b.kind] ?? b.kind}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-600">
                      {b.fileName ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{b.createdBy ?? '—'}</td>
                    <td className="px-4 py-2 text-gray-700">
                      {n('created') + n('updated') + n('applied')} importate · {n('unchanged')}{' '}
                      existente · {n('skipped')} sărite · {n('failed')} erori
                      {b.kind === 'reconciliere' && (
                        <Link
                          to={`/financials/bank-statements?source=import&importBatchId=${b.id}`}
                          className="ml-2 text-xs text-brand-700 hover:underline"
                        >
                          Vezi liniile
                        </Link>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
